import i18n from "i18next";

import {
  // This is fine because this file is for actions.
  __ONLY_WRITE_STATE_FROM_ACTIONS as writeState,
  readState,
} from "@/__main__/app-state.mjs";
import { IS_NODE, isPersistent, MAX_TIME } from "@/__main__/constants.mjs";
import getData, { postData, readData } from "@/__main__/get-data.mjs";
import { freshInitialState } from "@/__main__/initial-state.mjs";
import mainRefs from "@/__main__/refs.mjs";
import { setRoute } from "@/__main__/router.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import { appURLs } from "@/app/constants.mjs";
import {
  getActiveSessions,
  getUserRoles,
} from "@/feature-auth/fetches/api.mjs";
import * as GQL from "@/feature-auth/fetches/user-client-gql.mjs";
import ActiveSessionsModel from "@/feature-auth/models/active-sessions-model.mjs";
import UserRolesModel from "@/feature-auth/models/model-user-roles.mjs";
import type { AuthTokenObject } from "@/feature-auth/models/token-model.mjs";
import GetMeResponseModel from "@/feature-auth/models/user-get-me-result-model.mjs";
import type { NewUser, User } from "@/feature-auth/models/user-model.mjs";
import { authTokenHandler } from "@/feature-auth/utils/auth-token-handler.mjs";
import getBearerToken from "@/feature-auth/utils/get-auth-request-header.mjs";
import {
  setAdsFeatureFromUserRoles,
  setCMSFeature,
} from "@/feature-auth/utils/user-roles-change-listener.mjs";
import { isUserOnboarded } from "@/feature-auth/utils/utils.mjs";
import clone from "@/util/clone.mjs";
import { hasMarkup } from "@/util/constants.mjs";
import { appLog, devError } from "@/util/dev.mjs";
import sourceMap from "@/util/error-sourcemap.mjs";
import isRouteOverlay from "@/util/is-route-overlay.mjs";

const DEFAULT_AUTH_EXPIRY_6_MONTHS = Date.now() + 1000 * 60 * 60 * 24 * 30 * 6;

const authClientLoader = async () =>
  (await import("@/feature-auth/utils/auth-client.mjs")).AuthClient;

export const EVENT_USER_LOGOUT = "EVENT_USER_LOGOUT";

export async function setAuthAction(token: AuthTokenObject | null) {
  if (token) {
    await authTokenHandler.setToken(token);
  } else {
    await authTokenHandler.unsetToken();
  }
}

export function setUserAction(user: User | null) {
  if (user) {
    // Set the current user
    const newUser = clone(user);
    newUser[isPersistent] =
      readState.auth.token?.authTokenExpiry?.getTime?.() ??
      DEFAULT_AUTH_EXPIRY_6_MONTHS;
    writeState.user = newUser;
  } else {
    delete writeState.user;
    const settings = clone(freshInitialState.settings);
    settings[isPersistent] = MAX_TIME;
    writeState.settings = settings;
  }
}

let initAuthPromise;

/** This may return a partial user object if they have not been fully onboarded */
async function fetchTokenUser(): Promise<User | NewUser> {
  const bearerToken = await getBearerToken();

  const me = await postData(
    {
      url: appURLs.AUTH_GRAPHQL,
      body: {
        query: GQL.GetMe,
      },
    },
    GetMeResponseModel,
    undefined,
    { headers: { Authorization: bearerToken } },
  );

  return me;
}

export async function initAuth() {
  if (IS_NODE) return;

  if (!initAuthPromise) {
    initAuthPromise = (async () => {
      // We have to wait for render first, which fixes an issue where
      // React will complain about the DOM being different on hydration
      // React error #418
      if (hasMarkup) {
        await mainRefs.didRender;
      }

      const [token, user] = await Promise.all([
        authTokenHandler.getToken(),
        readData(["user"]),
      ]);

      if (token) {
        try {
          const user = await fetchTokenUser();

          if (isUserOnboarded(user)) {
            setUserAction(user);
          }
        } catch (e) {
          devError("failed to set user action", e);

          // Token not valid, force log out
          await logoutUser();
        }
      } else if (user) {
        // If we have no token but `user` is set, force log out
        await logoutUser();
      }

      setAdsFeatureFromUserRoles();
      setCMSFeature();

      // Temp fix for string birthdays
      if (typeof readState.user?.birthday === "string") {
        const newUser = clone(readState.user);
        newUser.birthday = new Date(newUser.birthday);
        setUserAction(newUser);
      }
    })();
  }

  await initAuthPromise;
}

export function resetInitAuth() {
  initAuthPromise = null;
}

export async function updateUserRoles() {
  const bearerToken = await getBearerToken();

  const result = await getData(getUserRoles(), UserRolesModel, undefined, {
    headers: { Authorization: bearerToken },
    networkBackOffTime: 1,
  });

  if (!(result instanceof Error)) {
    const user = clone(readState.user);
    user.roles = result;
    user[isPersistent] = MAX_TIME;
    writeState.user = user;
  }
}

export async function getActiveSessionsData() {
  const bearerToken = await getBearerToken();

  if (bearerToken) {
    try {
      const result = await getData(
        getActiveSessions(),
        ActiveSessionsModel,
        undefined,
        {
          headers: { Authorization: bearerToken },
        },
      );

      if (!(result instanceof Error)) {
        const user = clone(readState.user);
        user.activeSessions = result;
        user[isPersistent] = MAX_TIME;
        writeState.user = user;
      }
    } catch (e) {
      devError("Failed to get active sessions", e);
    }
  }
}

export async function changeEmail(newEmail: string, setEmails) {
  const bearerToken = await getBearerToken();

  if (bearerToken) {
    const AuthClient = await authClientLoader();
    await AuthClient.updateUserEmail(
      i18n.language,
      newEmail,
      bearerToken,
      setEmails,
    );
  }
}

export function updateUserEmailInState(newEmail: string) {
  const user = clone(readState.user);
  user.email = newEmail;
  user[isPersistent] = MAX_TIME;
  writeState.user = user;
}

/// SignOut user
export async function logoutUser() {
  if (isRouteOverlay()) return;
  const cause = await sourceMap(new Error()).catch(() => "unknown");
  appLog("[Auth] Logging out user", cause);
  try {
    const AuthClient = await authClientLoader();
    await AuthClient.invalidateToken();
  } catch (e) {
    devError("Failed to invalidate token", e);
  }

  setUserAction(null);
  setAuthAction(null);
  setRoute("/account", undefined, undefined, true);

  await eventBus.emit(EVENT_USER_LOGOUT);
}
